import { RefObject } from 'preact';
import { useState, useEffect } from 'preact/hooks';

type ElemDim<K extends string | number> = {
  itemKey: K | undefined;
  cumulativeWidth: number | undefined;
};

type CallBack<K extends string | number> = (key: K) => number;
export type useDetectOverflowProps<K extends string | number> = {
  rootRef: RefObject<HTMLDivElement>;
  getWidth: CallBack<K>;
  keysArray: (K | undefined)[];
};

/**
 * Implements overflow detection and calculation
 */
export function useDetectHorizontalOverflow<K extends string | number>(
  options: useDetectOverflowProps<K>
) {
  const { rootRef, getWidth, keysArray } = options;
  const [visibleItemKeys, setVisibleItemKeys] = useState<(K | undefined)[]>(keysArray);
  const [overflowItemKeys, setOverflowingItemKeys] = useState<(K | undefined)[]>();

  useEffect(() => {
    let resizeObserver: ResizeObserver;
    if (rootRef.current && keysArray && keysArray.length > 0) {
      const itemDimensions: ElemDim<K>[] = [];
      let sumofItemsWidth = 0;
      keysArray.forEach((itemKey) => {
        sumofItemsWidth += getWidth(itemKey as K);
        const elemDimension = {
          itemKey: itemKey,
          cumulativeWidth: sumofItemsWidth
        };
        itemDimensions.push(elemDimension as ElemDim<K>);
      });

      resizeObserver = new ResizeObserver(() => {
        const container = rootRef.current;
        const arr = itemDimensions?.map((item) => item.cumulativeWidth) as number[];
        if (
          container &&
          arr &&
          (container.clientWidth < arr[arr.length - 1] ||
            container.clientWidth < container.scrollWidth)
        ) {
          const { closestEntry, closestIndex } = searchClosest(arr, container?.clientWidth);
          if (closestEntry && closestIndex) {
            setVisibleItemKeys(keysArray.slice(0, closestIndex));
            setOverflowingItemKeys(keysArray?.slice(closestIndex));
          }
        } else {
          setVisibleItemKeys(keysArray);
          setOverflowingItemKeys([]);
        }
      });
      resizeObserver.observe(rootRef?.current as Element);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [keysArray, getWidth, rootRef]);

  return {
    visibleItemKeys,
    overflowItemKeys
  } as const;
}

const searchClosest = (
  arr: number[] | undefined,
  searchValue: number | undefined | null
): { closestEntry: number | null; closestIndex: number | null } => {
  {
    if (arr != null && searchValue != null) {
      let left = 0;
      let right = arr.length - 1;
      let closestEntry: number | null = null;
      let closestIndex: number | null = null;
      while (left <= right && left < arr.length && right > -1) {
        const mid = Math.floor((left + right) / 2);
        const midEntry = arr[mid];
        if (midEntry === searchValue) {
          return { closestEntry: midEntry!, closestIndex: mid! };
        } else if (midEntry < searchValue) {
          left = mid + 1;
        } else {
          right = mid - 1;
        }

        if (
          !closestEntry ||
          Math.abs(searchValue - midEntry) < Math.abs(searchValue - closestEntry)
        ) {
          closestEntry = midEntry as number;
          closestIndex = mid as number;
        }
      }
      return { closestEntry: closestEntry!, closestIndex: closestIndex! };
    } else return { closestEntry: null, closestIndex: null };
  }
};
